import CardData from './CardData';
import type { CardProperties, CardRawData } from './CardData';
import type { GeoFieldPoint } from './GeoLocation';
import StageData from './StageData';
import type { StageListItem } from './StageData';
import RouteData from './RouteData';
import type { RouteListItem } from './RouteData';
import styles from "../components/CardInfo.module.scss";
import i18n from "../i18n";
import ExternalHTMLContent from "../components/ExternalHTMLContent";
import {LocaleService} from "../common/LocaleService";

/** Valor numerico que el backend envia envuelto. */
interface RatingValue {
    value: string | number;
}

/** Tramo del recorrido. Los mapas vienen indexados por claves tipo 'localidades_1'. */
export interface PathStep {
    distance?: string | number;
    title?: string;
    population_str?: string;
    services_html?: string[];
    items?: Record<string, { length?: string | number }>;
    length?: string | number;
}

/** Datos crudos de una etapa, tal cual llegan del detalle REST. */
export interface StageCardRawData extends CardRawData {
    track_id?: string;
    title_prefix?: string;
    length_km?: string | number;
    duration_seconds?: string | number;
    texto_intro?: { value: string };
    field_rating_difficulty?: RatingValue[];
    field_rating_landscape?: RatingValue[];
    /** Siempre presente: se asigna y se recorre con Object.keys sin comprobar. */
    path_steps: Record<string, PathStep>;
    gronze_desnivel?: { file?: { url?: string } };
    geo_elevation_gain?: string | number;
    geo_elevation_loss?: string | number;
    route_ref?: RouteListItem;
    stage_next_list?: StageListItem[];
    stage_prev_list?: StageListItem[];
}

/**
 * CardData / Stage Card - Etapas
 *
 * Data wrapper for object types:
 * - Stage
 */
class StageCard extends CardData {

    // Solo estrechan el tipo de campos de CardData: `declare` no emite nada.
    declare data?: StageCardRawData;
    declare path_steps: Record<string, PathStep>;

    /** Desnivel acumulado, en metros. */
    geo_elevation_gain?: string | number;
    geo_elevation_loss?: string | number;

    constructor(type: string, properties: CardProperties, data?: StageCardRawData) {
      // Call parent constructor.
      super(type, properties, data);
      
      // Add extra properties.
      if (data) {
        if (!data.geo_location && data.geo_bounds && data.geo_bounds.lat && data.geo_bounds.lon) {
          // Geo bounds contains center lat / lon so we can just...
          // La guarda de arriba comprueba que lat y lon estan presentes.
          this.geo_location = data.geo_bounds as GeoFieldPoint;
        }
        if (data.title_prefix) {
          this.subtitle = LocaleService.decodeHtmlEntities(data.title_prefix);
        }

        this.lengthKm = this.getLengthKm(data.length_km);
        this.duration = this.getDuration(data.duration_seconds);
        this.texto_intro = data.texto_intro?.value;
        this.difficulty = this.getDifficulty(data.field_rating_difficulty);
        this.landscape = this.getLandscape(data.field_rating_landscape);
        this.path_steps = data.path_steps;
        this.elevationImage = data.gronze_desnivel?.file?.url;

        this.geo_elevation_gain = data.geo_elevation_gain;
        this.geo_elevation_loss = data.geo_elevation_loss;
      }
    }

    /**
     * Returns track_id
     */
    getMapId(): string | null | undefined {
      return this.data ? this.data.track_id : null;
    }
    
    /**
     * Gets Route Data.
     */
    getRouteData(): RouteData | undefined {
      if (this.data && this.data.route_ref) {
        return RouteData.fromListItem(this.data.route_ref);
      }
      return undefined;
    }
    
    /**
     * Overrides CardData.getSubtitle().
     */
    getSubtitle(): string | undefined {
      const route = this.getRouteData();
      const parts = [];
      if (this.data && this.data.title_prefix) {
        parts.push(this.data.title_prefix);
      }
      
      if (route) {
        parts.push(route.name);
      }
      if (parts.length) {
        return parts.join(' / ');
      }
      return undefined;
    }
    
    /**
     * Stage cards can be selected.
     */
    canSelect(): boolean {
      return true;
    }

    /**
     * Navigation: has next?.
     *
     * Devuelve un valor veraz/falso: si no hay data devuelve undefined.
     */
    hasNext() {
        return this.data && this.data.stage_next_list && this.data.stage_next_list.length > 0;
    }

    /**
     * Navigation: has previous?.
     *
     * @see hasNext
     */
    hasPrev() {
        return this.data && this.data.stage_prev_list && this.data.stage_prev_list.length > 0;
    }

    /**
     * Navigation: Get next.
     */
    getNext(): StageData | undefined {
      // Un array vacio es truthy: comprobar solo la lista dejaba pasar un
      // undefined a fromListItem, que reventaba al leer item.id.
      const nextStage = this.data?.stage_next_list?.[0];
      return nextStage ? StageData.fromListItem(nextStage) : undefined;
    }
    
    /**
     * Navigation: Get previous.
     */
    getPrev(): StageData | undefined {
      // @see getNext
      const prevStage = this.data?.stage_prev_list?.[0];
      return prevStage ? StageData.fromListItem(prevStage) : undefined;
    }

    /**
     * Converts a length in kms to a formatted string (e.g., "29.00 km").
     */
    getLengthKm(length_km?: string | number): string {
        // Las comparaciones con cadena ya hacian esta conversion implicitamente.
        if (!length_km || Number(length_km) <= 0) {
            return '-';
        }

        return Number(length_km).toFixed(1) + ' km';
    }

    /**
     * Converts a duration in seconds to a formatted string (e.g., "4H 7min").
     */
    getDuration(seconds?: string | number): string {
        if (!seconds || Number(seconds) <= 0) {
            return '-';
        }

        const hours = Math.floor(Number(seconds) / 3600);
        const minutes = Math.floor((Number(seconds) % 3600) / 60);

        const hoursPart = hours > 0 ? `${hours} h` : '';
        const minutesPart = minutes > 0 ? `${minutes} min` : '';

        return `${hoursPart} ${minutesPart}`.trim();
    }

    /**
     * Converts the field_rating_difficulty to number
     */
    getDifficulty(field_rating_difficulty?: RatingValue[]): number {
        if (!field_rating_difficulty || field_rating_difficulty.length === 0) {
            return 0;
        }

        return Number(field_rating_difficulty[0].value);
    }

    /**
     * Converts the field_rating_landscape to number
     */
    getLandscape(field_rating_landscape?: RatingValue[]): number {
        if (!field_rating_landscape || field_rating_landscape.length === 0) {
            return 0;
        }

        return Number(field_rating_landscape[0].value);
    }

    static createStars(rating: number): string {
        let stars = '';
        for (let i = 1; i < 6; i++) {
            if (i > rating) {
                stars += '<span class="star off"></span>';
            } else {
                stars += '<span class="star on"></span>';
            }
        }
        return `<div class="rating">${stars}</div>`;
    }

    static normalizeData (data?: string | number): string | number {
        if (!data)
            return '';
        // isNaN ya convertia el argumento a numero de forma implicita.
        if (!isNaN(Number(data))) {
            return data;
        }
        return data.toLocaleString('es-ES');
    }

    /**
     * Formatea un número de elevación con separador de miles (punto)
     *
     * @param elevation Elevación en metros
     * @return Número formateado (ej: 1.258)
     */
    static formatElevation(elevation?: string | number): string {
        if (!elevation || Number(elevation) <= 0) {
            return '-';
        }
        return Number(elevation).toLocaleString('es-ES', {
            useGrouping: true,
            maximumFractionDigits: 0
        });
    }

    static getPathSteps (pathSteps: Record<string, PathStep>) {
        const stepDOM = Object.keys(pathSteps).map((key) => {
            const step = pathSteps[key];
            if (key.includes('localidades')) {
                return <div key={key} className={styles.stageRow}>
                    <div className={styles.place}>
                        <span className={styles.distance}>{this.normalizeData(step.distance)}</span>
                        <span className={styles.title}>{this.normalizeData(step.title)}</span>
                        <span className={styles.population}>
                            <span><i>{step.population_str}</i></span>
                            {(step.population_str || Number(step.population_str) !== 0) &&
                                <i className={styles.icon + " glyphicons glyphicons-user"}/>}
                        </span>
                    </div>
                    <div className={styles.serviceContainer}>
                        {
                            step.services_html?.map((service, index) =>
                                <span className={styles.service} key={"service_" + index}>
                                    <ExternalHTMLContent contentHtml={service}/>
                                </span>
                            )
                        }
                    </div>
                </div>;
            } else if (key.includes('multiple')) {
                const items = step.items!;
                const stepItems = Object.keys(items).filter(key => key.includes('step'));
                // Div vacío para no descolocar el grid: cada localidad necesita su .step detrás.
                if (stepItems.length === 0)
                    return <div key={key} className={styles.step}/>;
                return <div key={key} className={styles.step}><span>{this.normalizeData(items[stepItems[0]].length)}</span></div>;
            } else {
                return <div key={key} className={styles.step}><span>{this.normalizeData(step.length)}</span></div>;
            }
        });
        return stepDOM;
    }

    static getCardDOM (cardData: StageCard) {
        const stageContent = <div>
            <div className={styles.stageTable}>
                <div className={styles.row}>
                    <div>
                        <strong>{i18n.t('cardContent.stage.distance')} </strong>
                        {cardData.lengthKm}
                    </div>
                    <div>
                        <strong>{i18n.t('cardContent.stage.duration')} </strong>
                        {cardData.duration}
                    </div>
                </div>
                {(cardData.geo_elevation_gain || cardData.geo_elevation_loss) && (
                    <div className={styles.row}>
                        <div>
                            <strong>{i18n.t('cardContent.stage.elevation_gain')}: </strong>
                            <i className="glyphicons glyphicons-arrow-up elevationIcon" />{this.formatElevation(cardData.geo_elevation_gain)} m
                            {' '}
                            <i className="glyphicons glyphicons-arrow-down elevationIcon" />{this.formatElevation(cardData.geo_elevation_loss)} m
                        </div>
                    </div>
                )}
                <div className={styles.row}>
                    <div className="stage-rating card-rating difficulty">
                        <strong>{i18n.t('cardContent.stage.difficulty')} </strong>
                        <ExternalHTMLContent contentHtml={this.createStars(cardData.difficulty)} />
                    </div>
                    <div className="stage-rating card-rating landscape">
                        <strong>{i18n.t('cardContent.stage.landscape')} </strong>
                        <ExternalHTMLContent contentHtml={this.createStars(cardData.landscape)} />
                    </div>
                </div>
            </div>
            <div className={styles.placesContainer}>{ this.getPathSteps(cardData.path_steps) }</div>
        </div>
        return stageContent;
    }
}

export default StageCard;
